import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { appointment } from '@solimat/solimat-web-endpoint';
import CalendarEmptyIcon from '../../../img/NewDesign/img/calendar-empty.svg?react';
import './NextAppointmentNew.scss';

const BASE_URL = import.meta.env.VITE_REACT_APP_CITAS_PATH || '';
const FUNCTIONS_KEY = import.meta.env.VITE_REACT_APP_CITAS_KEY || '';
const apiAppointment = new appointment.AppointmeService(BASE_URL, FUNCTIONS_KEY);

interface IDoctor { nombre: string; }
interface ICentro {
  centroId: string; nombre: string; descripcion: string; codigoPostal: string;
  provincia: string; localidad: string; tipoCalle: string; nombreCalle: string;
  numeroCalle: string; letra: string; piso: string; latitud: number; longitud: number; telefono: string;
}
interface INextAppointment {
  id: string; citaId: string; userId: string; personaId: string; fecha: string;
  mensaje: string; nombrePrueba: string; especialidad: string; tipoActividad: string;
  estado: string; realizada: boolean; reprogramada: boolean; incomparecencia: boolean;
  doctor: IDoctor; centro: ICentro; linkVideoConsulta: string;
}

const EMPTY_APPOINTMENT: INextAppointment = {
  id: '', citaId: '', userId: '', personaId: '', fecha: '', mensaje: '',
  nombrePrueba: '', especialidad: '', tipoActividad: '', estado: '',
  realizada: true, reprogramada: false, incomparecencia: false,
  doctor: { nombre: 'Doctor' },
  centro: {
    centroId: '', nombre: '', descripcion: '', codigoPostal: '', provincia: '',
    localidad: '', tipoCalle: '', nombreCalle: '', numeroCalle: '', letra: '',
    piso: '', latitud: 0, longitud: 0, telefono: '',
  },
  linkVideoConsulta: '',
};

// DEV-ONLY: set to true to preview the card with appointment data
const USE_MOCK = false;
const MOCK_APPOINTMENT: INextAppointment = {
  id: 'mock-001',
  citaId: 'cita-001',
  userId: 'user-001',
  personaId: 'persona-001',
  fecha: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(), // en 2 días
  mensaje: '',
  nombrePrueba: 'Revisión traumatológica',
  especialidad: 'Traumatología',
  tipoActividad: 'Consulta',
  estado: 'Pendiente',
  realizada: false,
  reprogramada: false,
  incomparecencia: false,
  doctor: { nombre: 'Dra. L. Romero' },
  centro: {
    centroId: 'centro-001',
    nombre: 'Hospital San José de Solimat',
    descripcion: 'Hospital San José de Solimat',
    codigoPostal: '02006',
    provincia: 'Albacete',
    localidad: 'Albacete',
    tipoCalle: 'C/',
    nombreCalle: 'Hermanos Falcó',
    numeroCalle: '37',
    letra: '',
    piso: '',
    latitud: 38.9942,
    longitud: -1.8564,
    telefono: '967511417',
  },
  linkVideoConsulta: '',
};

interface NextAppointmentNewProps {
  userId?: string;
  userName?: string;
  onNav?: (section: string) => void;
}

const NextAppointmentNew: React.FC<NextAppointmentNewProps> = ({ userId, userName = 'Paciente', onNav }) => {
  const [nextAppointment, setNextAppointment] = useState<INextAppointment>(EMPTY_APPOINTMENT);
  const [resolved, setResolved] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (USE_MOCK) {
      setNextAppointment(MOCK_APPOINTMENT);
      setResolved(true);
      return;
    }
    if (!userId) return;
    apiAppointment
      .getNextAppointment(userId)
      .then((data: any) => {
        if (data.data?.rows?.length) {
          setNextAppointment(data.data.rows[0]);
        }
        setResolved(true);
      })
      .catch((err: any) => {
        console.log(err);
        setResolved(true);
      });
  }, [userId]);

  const hasAppt = resolved && !!nextAppointment.id;

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h >= 6 && h < 14) return 'Buenos días';
    if (h >= 14 && h < 20) return 'Buenas tardes';
    return 'Buenas noches';
  };

  const handleNav = (section: string) => {
    if (onNav) onNav(section);
    else navigate(`/${section}`);
  };

  const _fecha = nextAppointment.fecha ? new Date(nextAppointment.fecha) : null;
  const hour = _fecha ? _fecha.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) + ' h' : '';
  const date = _fecha ? _fecha.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' }) : '';
  const centro = nextAppointment.linkVideoConsulta ? 'Video Consulta' : nextAppointment.centro.descripcion || nextAppointment.centro.nombre;

  return (
    <section className="hero">
      <div className="container">
        <div className="hero__grid">

          {/* Greeting */}
          <div className="hero__greeting">
            <span className="eyebrow">{getGreeting()}</span>
            <h1>Hola, <em>{userName}</em>.<br />Aquí está tu día.</h1>
            <p>
              {hasAppt
                ? 'Tu próxima cita está a la vuelta de la esquina. Si necesitas algo más, tu gestor está disponible.'
                : 'Hoy no tienes ninguna cita programada. Si quieres pedir una o tienes dudas, tu gestor está disponible.'}
            </p>
            <div className="hero__greeting-actions">
              <button className="hero-btn hero-btn--outline" onClick={() => handleNav('comunicaciones')}>
                <span className="e-icons e-comment-show"></span>
                Contactar gestor
              </button>
            </div>
          </div>

          {/* Appointment card */}
          <div className={`hero__appt${hasAppt ? '' : ' hero__appt--empty'}`}>
            {hasAppt ? (
              <>
                <div className="hero__appt-eyebrow">Próxima cita</div>
                <div className="hero__appt-title">{nextAppointment.especialidad || nextAppointment.nombrePrueba}</div>
                <div className="hero__appt-list">
                  <div className="hero__appt-item"><div className="k">Fecha</div><div className="v">{date}</div></div>
                  <div className="hero__appt-item"><div className="k">Hora</div><div className="v">{hour}</div></div>
                  <div className="hero__appt-item"><div className="k">Centro</div><div className="v">{centro}</div></div>
                  <div className="hero__appt-item"><div className="k">Dr.</div><div className="v">{nextAppointment.doctor.nombre}</div></div>
                </div>
                <div className="hero__appt-actions">
                  <button className="hero-btn hero-btn--white" onClick={() => handleNav(`mis-citas/${nextAppointment.id}`)}>Ver detalles</button>
{/*                   <button className="hero-btn hero-btn--ghost" onClick={() => handleNav('mis-citas')}>Reprogramar</button>
 */}                </div>
              </>
            ) : (
              <div className="hero__appt-empty-content">
                <div className="hero__appt-eyebrow">Sin citas próximas</div>
                <div className="hero__appt-emptyglyph" aria-hidden>
                  <CalendarEmptyIcon width={64} height={64} aria-hidden="true" />
                </div>
                <div className="hero__appt-emptytitle">No tienes citas programadas</div>
                <div className="hero__appt-emptysub">
                  Cuando tu gestor agende una nueva cita aparecerá aquí.  Si quieres pedir una o tienes dudas, tu gestor está disponible.
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </section>
  );
};

export default NextAppointmentNew;
